import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, storage } from '../firebase-config';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp, doc, runTransaction, getDoc,
  increment, arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { key: 'food_quality', label: 'Food Quality', emoji: '🍽️', defaultPriority: 'high' },
  { key: 'hygiene', label: 'Hygiene & Cleanliness', emoji: '🧹', defaultPriority: 'high' },
  { key: 'infrastructure', label: 'Infrastructure', emoji: '🏗️', defaultPriority: 'medium' },
  { key: 'staff_behavior', label: 'Staff Behavior', emoji: '👤', defaultPriority: 'high' },
  { key: 'noise', label: 'Noise Disturbance', emoji: '🔊', defaultPriority: 'low' },
  { key: 'security', label: 'Security', emoji: '🔒', defaultPriority: 'high' },
  { key: 'internet', label: 'Internet & Wi-Fi', emoji: '📶', defaultPriority: 'medium' },
  { key: 'laundry', label: 'Laundry', emoji: '👕', defaultPriority: 'low' },
  { key: 'water_supply', label: 'Water Supply', emoji: '💧', defaultPriority: 'high' },
  { key: 'electricity', label: 'Electricity', emoji: '⚡', defaultPriority: 'high' },
  { key: 'other', label: 'Other', emoji: '📩', defaultPriority: 'medium' },
];

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', emoji: '📬', color: 'slate', step: 0 },
  acknowledged: { label: 'Acknowledged', emoji: '👀', color: 'blue', step: 1 },
  in_progress: { label: 'In Progress', emoji: '🔧', color: 'amber', step: 2 },
  resolved: { label: 'Resolved', emoji: '✅', color: 'emerald', step: 3 },
  rejected: { label: 'Rejected', emoji: '❌', color: 'red', step: -1 },
};

const STATUS_STYLES = {
  submitted: 'bg-slate-500/10 text-slate-400 border border-slate-500/20',
  acknowledged: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
  resolved: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  rejected: 'bg-red-500/10 text-red-400 border border-red-500/20',
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', emoji: '🟢' },
  medium: { label: 'Medium', emoji: '🟡' },
  high: { label: 'High', emoji: '🔴' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(ts) {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

async function generateComplaintNumber(db) {
  const counterRef = doc(db, 'counters', 'complaints');
  let num = 1;
  try {
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(counterRef);
      if (snap.exists()) {
        num = (snap.data().count || 0) + 1;
        transaction.update(counterRef, { count: increment(1) });
      } else {
        num = 1;
        transaction.set(counterRef, { count: 1 });
      }
    });
  } catch {
    num = Math.floor(Date.now() % 100000);
  }
  return `CPL-${String(num).padStart(5, '0')}`;
}

// ─── Main ComplaintBox Component ──────────────────────────────────────────────

export default function ComplaintBox({ userProfile }) {
  const auth = getAuth();
  const user = auth.currentUser;

  const [view, setView] = useState('list');
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const [category, setCategory] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const q = query(
      collection(db, 'complaints'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setComplaints(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
      if (selectedComplaint?.id) {
        const updated = snap.docs.find(d => d.id === selectedComplaint.id);
        if (updated) setSelectedComplaint({ id: updated.id, ...updated.data() });
      }
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    const cat = CATEGORIES.find(c => c.key === category);
    if (cat) setPriority(cat.defaultPriority);
  }, [category]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    addMediaFiles(files);
  }, [mediaFiles]);

  const addMediaFiles = (files) => {
    const remaining = 5 - mediaFiles.length;
    files.slice(0, remaining).forEach(file => {
      const type = file.type.startsWith('video/') ? 'video' : 'image';
      const preview = type === 'image' ? URL.createObjectURL(file) : null;
      setMediaFiles(prev => [...prev, { file, preview, type, progress: 0, url: null }]);
    });
  };

  const removeMedia = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || description.trim().length < 20) return;
    if (category === 'other' && !customTitle.trim()) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const uploadedUrls = [];
      const uploadedTypes = [];
      for (let i = 0; i < mediaFiles.length; i++) {
        const mf = mediaFiles[i];
        const storageRef = ref(storage, `complaints/${user.uid}/${Date.now()}_${i}_${mf.file.name}`);
        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, mf.file);
          task.on('state_changed',
            (snap) => {
              const pct = Math.round(snap.bytesTransferred / snap.totalBytes * 100);
              setMediaFiles(prev => prev.map((m, idx) => idx === i ? { ...m, progress: pct } : m));
            },
            reject,
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              uploadedUrls.push(url);
              uploadedTypes.push(mf.type);
              resolve();
            }
          );
        });
      }

      const complaintNumber = await generateComplaintNumber(db);
      const catInfo = CATEGORIES.find(c => c.key === category);
      const now = serverTimestamp();
      const displayTitle = category === 'other' ? customTitle.trim() : (catInfo?.label ?? 'Complaint');

      await addDoc(collection(db, 'complaints'), {
        complaintNumber,
        category,
        title: displayTitle,
        description: description.trim(),
        mediaUrls: uploadedUrls,
        mediaTypes: uploadedTypes,
        status: 'submitted',
        priority,
        userId: user.uid,
        userName: isAnonymous ? 'Anonymous' : (userProfile?.name || user.displayName || 'Unknown'),
        userEmail: userProfile?.email || user.email || '',
        instituteId: userProfile?.instituteId || '',
        hostel: userProfile?.hostel || '',
        source: 'web_dashboard',
        isAnonymous,
        upvoteCount: 0,
        upvotedBy: [],
        adminComment: null,
        assignedTo: null,
        viewCount: 0,
        createdAt: now,
        updatedAt: now,
        resolvedAt: null,
        statusHistory: [{ status: 'submitted', changedAt: now, changedBy: 'user', comment: 'Complaint submitted.' }],
      });

      setCategory('');
      setCustomTitle('');
      setDescription('');
      setPriority('medium');
      setIsAnonymous(false);
      setMediaFiles([]);
      setView('list');
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (complaintId) => {
    if (!user?.uid) return;
    const ref2 = doc(db, 'complaints', complaintId);
    try {
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(ref2);
        const upvotedBy = snap.data()?.upvotedBy || [];
        if (upvotedBy.includes(user.uid)) return;
        transaction.update(ref2, {
          upvoteCount: increment(1),
          upvotedBy: arrayUnion(user.uid),
          updatedAt: serverTimestamp(),
        });
      });
    } catch { }
  };

  const filtered = complaints.filter(c => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'open') return ['submitted', 'acknowledged'].includes(c.status);
    if (filterStatus === 'in_progress') return c.status === 'in_progress';
    if (filterStatus === 'resolved') return c.status === 'resolved';
    return true;
  });

  const stats = {
    open: complaints.filter(c => ['submitted', 'acknowledged'].includes(c.status)).length,
    inProgress: complaints.filter(c => c.status === 'in_progress').length,
    resolved: complaints.filter(c => c.status === 'resolved').length,
  };

  return (
    <div className="max-w-2xl mx-auto text-white">
      {/* Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[9999]" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Full view" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          <button className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/15 border-none text-white cursor-pointer text-base flex items-center justify-center" onClick={() => setLightboxUrl(null)}>✕</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-3">
        {view !== 'list' && (
          <button className="bg-transparent border-none text-amber-400 cursor-pointer font-semibold text-sm p-0 mb-2 mt-1 hover:text-amber-300" onClick={() => { setView('list'); setSelectedComplaint(null); }}>
            ← Back
          </button>
        )}
        <div>
          <h2 className="m-0 text-xl font-extrabold text-white">
            {view === 'list' ? '📬 Complaint Box' : view === 'create' ? '✍️ New Complaint' : `📋 ${selectedComplaint?.complaintNumber || 'Complaint'}`}
          </h2>
          <p className="mt-1 mb-0 text-xs text-slate-500">
            {view === 'list' ? 'Lodge and track your hostel complaints here.' : view === 'create' ? 'Submit a new hostel/mess complaint.' : 'Complaint Details'}
          </p>
        </div>
        {view === 'list' && (
          <button className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black border-none rounded-xl font-bold cursor-pointer whitespace-nowrap text-sm transition-colors" onClick={() => setView('create')}>+ New</button>
        )}
      </div>

      {/* LIST VIEW */}
      {view === 'list' && (
        <div>
          {complaints.length > 0 && (
            <div className="flex gap-2.5 mb-4">
              {[
                { label: 'Open', count: stats.open, cls: 'border-emerald-500/20 text-emerald-400' },
                { label: 'In Progress', count: stats.inProgress, cls: 'border-amber-500/20 text-amber-400' },
                { label: 'Resolved', count: stats.resolved, cls: 'border-teal-500/20 text-teal-400' },
              ].map(s => (
                <div key={s.label} className={`flex-1 bg-slate-800/40 border rounded-xl p-3 text-center ${s.cls}`}>
                  <div className="text-xl font-extrabold">{s.count}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {complaints.length > 0 && (
            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                { key: 'all', label: 'All' },
                { key: 'open', label: '📬 Open' },
                { key: 'in_progress', label: '🔧 In Progress' },
                { key: 'resolved', label: '✅ Resolved' },
              ].map(f => (
                <button
                  key={f.key}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-colors border ${filterStatus === f.key ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' : 'bg-slate-800/40 border-slate-700/30 text-slate-500 hover:text-slate-400'}`}
                  onClick={() => setFilterStatus(f.key)}
                >{f.label}</button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-9 h-9 border-3 border-slate-700 border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState onSubmit={() => setView('create')} hasComplaints={complaints.length > 0} />
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map(c => (
                <ComplaintCard
                  key={c.id}
                  complaint={c}
                  uid={user?.uid}
                  onOpen={() => { setSelectedComplaint(c); setView('detail'); }}
                  onUpvote={() => handleUpvote(c.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* CREATE VIEW */}
      {view === 'create' && (
        <CreateForm
          category={category} setCategory={setCategory}
          customTitle={customTitle} setCustomTitle={setCustomTitle}
          description={description} setDescription={setDescription}
          priority={priority} setPriority={setPriority}
          isAnonymous={isAnonymous} setIsAnonymous={setIsAnonymous}
          mediaFiles={mediaFiles} addMediaFiles={addMediaFiles} removeMedia={removeMedia}
          handleDrop={handleDrop} dropRef={dropRef}
          submitting={submitting} submitError={submitError}
          onSubmit={handleSubmit}
        />
      )}

      {/* DETAIL VIEW */}
      {view === 'detail' && selectedComplaint && (
        <DetailView
          complaint={selectedComplaint}
          uid={user?.uid}
          onUpvote={() => handleUpvote(selectedComplaint.id)}
          onLightbox={setLightboxUrl}
        />
      )}
    </div>
  );
}

// ─── Complaint Card ───────────────────────────────────────────────────────────

function ComplaintCard({ complaint: c, uid, onOpen, onUpvote }) {
  const cat = CATEGORIES.find(x => x.key === c.category);
  const statusCfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.submitted;
  const alreadyUpvoted = c.upvotedBy?.includes(uid);

  return (
    <div className="bg-slate-800/40 border border-slate-700/30 rounded-2xl p-4 cursor-pointer transition-all hover:border-slate-600/40" onClick={onOpen}>
      <div className="flex items-center gap-2.5 mb-2">
        <span className="text-xl flex-shrink-0">{cat?.emoji || '📩'}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-amber-400 font-semibold">{c.complaintNumber}</div>
          <div className="font-bold text-white truncate">{c.title || cat?.label || 'Complaint'}</div>
        </div>
        <span className={`px-2.5 py-0.5 rounded-lg text-[11px] font-bold whitespace-nowrap flex-shrink-0 ${STATUS_STYLES[c.status] || STATUS_STYLES.submitted}`}>
          {statusCfg.emoji} {statusCfg.label}
        </span>
      </div>
      <div className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-2.5">{c.description}</div>
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {c.upvoteCount > 0 && (
            <span
              onClick={e => { e.stopPropagation(); if (!alreadyUpvoted && c.userId !== uid) onUpvote(); }}
              className={`text-xs flex items-center gap-1 ${alreadyUpvoted ? 'text-amber-400' : 'text-slate-500'} ${c.userId === uid || alreadyUpvoted ? 'cursor-default' : 'cursor-pointer hover:text-amber-400'}`}
            >
              👍 {c.upvoteCount}
            </span>
          )}
          {c.mediaUrls?.length > 0 && <span className="text-xs text-slate-500">📎 {c.mediaUrls.length}</span>}
        </div>
        <span className="text-[11px] text-slate-600">{timeAgo(c.createdAt)}</span>
      </div>
    </div>
  );
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function DetailView({ complaint: c, uid, onUpvote, onLightbox }) {
  const cat = CATEGORIES.find(x => x.key === c.category);
  const statusCfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.submitted;
  const isOwn = c.userId === uid;
  const alreadyUpvoted = c.upvotedBy?.includes(uid);
  const steps = ['submitted', 'acknowledged', 'in_progress', 'resolved'];
  const currentStep = steps.indexOf(c.status);

  return (
    <div className="flex flex-col gap-4">
      {c.status === 'rejected' && (
        <div className="flex items-start gap-2 p-3.5 rounded-xl bg-red-500/8 border border-red-500/20">
          <strong className="text-red-400">❌ Complaint Rejected</strong>
          <p className="m-0 mt-1 text-slate-400 text-xs">{c.adminComment || 'This complaint was rejected by the admin.'}</p>
        </div>
      )}

      {c.status !== 'rejected' && (
        <div className="flex items-start bg-slate-800/40 border border-slate-700/30 rounded-2xl p-4">
          {steps.map((step, i) => {
            const cfg = STATUS_CONFIG[step];
            const done = i <= currentStep;
            const active = i === currentStep;
            return (
              <React.Fragment key={step}>
                <div className="text-center flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs text-white mx-auto transition-all ${done ? 'bg-amber-500' : 'bg-slate-800'} ${active ? 'ring-2 ring-amber-400 scale-110' : ''}`}>
                    {done && !active ? '✓' : active ? '●' : ''}
                  </div>
                  <div className={`text-[10px] mt-1 ${active ? 'text-amber-400' : done ? 'text-slate-500' : 'text-slate-700'}`}>{cfg.label}</div>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-[0.5] h-0.5 mt-3.5 ${i < currentStep ? 'bg-amber-500' : 'bg-slate-800'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Complaint Info */}
      <div className="bg-slate-800/40 border border-slate-700/30 rounded-2xl p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="text-2xl">{cat?.emoji || '📩'}</span>
          <div>
            <div className="font-bold text-white">{c.title || cat?.label}</div>
            <div className="text-xs text-amber-400">{c.complaintNumber} · {timeAgo(c.createdAt)}</div>
          </div>
        </div>
        <hr className="border-0 border-t border-slate-700/30 my-3" />
        <p className="text-slate-400 leading-relaxed m-0 text-sm">{c.description}</p>
        <div className="flex gap-2 mt-3 flex-wrap">
          <span className="px-2.5 py-0.5 bg-slate-700/30 rounded-lg text-xs text-slate-400">{PRIORITY_CONFIG[c.priority]?.emoji} {c.priority}</span>
          {c.isAnonymous && <span className="px-2.5 py-0.5 bg-slate-700/30 rounded-lg text-xs text-slate-400">👤 Anonymous</span>}
          {c.mediaUrls?.length > 0 && <span className="px-2.5 py-0.5 bg-slate-700/30 rounded-lg text-xs text-slate-400">📎 {c.mediaUrls.length} media</span>}
        </div>
      </div>

      {/* Media Gallery */}
      {c.mediaUrls?.length > 0 && (
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Attachments</div>
          <div className="flex gap-2.5 flex-wrap">
            {c.mediaUrls.map((url, i) => {
              const isVideo = c.mediaTypes?.[i] === 'video';
              return isVideo ? (
                <video key={i} src={url} controls className="w-24 h-24 object-cover rounded-xl" />
              ) : (
                <img key={i} src={url} alt={`Attachment ${i + 1}`} className="w-24 h-24 object-cover rounded-xl cursor-pointer hover:opacity-80 transition-opacity" onClick={() => onLightbox(url)} />
              );
            })}
          </div>
        </div>
      )}

      {/* Admin comment */}
      {c.adminComment && (
        <div className="flex items-start gap-2 p-3.5 rounded-xl bg-amber-500/5 border border-amber-500/15">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center font-bold text-xs text-black">R</div>
            <strong className="text-amber-400 text-sm">Resido Admin</strong>
          </div>
          <p className="m-0 text-slate-400 leading-relaxed text-sm">{c.adminComment}</p>
        </div>
      )}

      {/* Upvote */}
      {!isOwn && !['resolved', 'rejected'].includes(c.status) && (
        <button
          className={`w-full py-3 rounded-xl font-semibold cursor-pointer text-sm transition-colors border ${alreadyUpvoted ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-800/40 border-slate-700/30 text-slate-400 hover:border-amber-500/30 hover:text-amber-400'}`}
          onClick={alreadyUpvoted ? undefined : onUpvote}
          disabled={alreadyUpvoted}
        >
          {alreadyUpvoted ? `👍 Upvoted (${c.upvoteCount})` : `👍 Same issue? Upvote (${c.upvoteCount})`}
        </button>
      )}
    </div>
  );
}

// ─── Create Form ──────────────────────────────────────────────────────────────

function CreateForm({ category, setCategory, customTitle, setCustomTitle, description, setDescription, priority, setPriority, isAnonymous, setIsAnonymous, mediaFiles, addMediaFiles, removeMedia, handleDrop, dropRef, submitting, submitError, onSubmit }) {
  const canSubmit = category && description.trim().length >= 20 && (category !== 'other' || customTitle.trim()) && !submitting;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      {/* Category */}
      <div>
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Category *</div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-2">
          {CATEGORIES.map(cat => (
            <div
              key={cat.key}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-colors border ${category === cat.key ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/40 border-slate-700/30 hover:border-slate-600/40'}`}
              onClick={() => setCategory(cat.key)}
            >
              <span className="text-base">{cat.emoji}</span>
              <span className={`text-xs ${category === cat.key ? 'text-amber-400' : 'text-slate-500'}`}>{cat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {category === 'other' && (
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Title *</div>
          <input className="w-full bg-slate-800/40 border border-slate-700/30 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-amber-500/40 transition-colors" placeholder="Brief title (max 80 chars)" value={customTitle} onChange={e => setCustomTitle(e.target.value.slice(0, 80))} />
        </div>
      )}

      {/* Priority */}
      <div>
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Priority</div>
        <div className="flex gap-2">
          {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
            <button
              type="button"
              key={key}
              className={`flex-1 py-2 px-2.5 rounded-xl font-semibold text-xs cursor-pointer transition-colors border ${priority === key ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-800/40 border-slate-700/30 text-slate-500 hover:text-slate-400'}`}
              onClick={() => setPriority(key)}
            >
              {cfg.emoji} {cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Description *</div>
        <textarea
          className="w-full bg-slate-800/40 border border-slate-700/30 rounded-xl px-3 py-2.5 text-white text-sm outline-none resize-y font-[inherit] focus:border-amber-500/40 transition-colors"
          placeholder="Describe your complaint in detail (min 20 characters)..."
          value={description}
          onChange={e => setDescription(e.target.value.slice(0, 2000))}
          rows={5}
        />
        <div className="text-right text-[11px] text-slate-600">{description.length}/2000</div>
      </div>

      {/* Media Upload */}
      <div>
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Photos / Videos (optional, max 5)</div>
        <div
          ref={dropRef}
          className="border-2 border-dashed border-slate-700/30 rounded-xl p-5 cursor-pointer min-h-[100px] flex items-center justify-center bg-slate-800/30 hover:border-slate-600/40 transition-colors"
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => document.getElementById('media-input')?.click()}
        >
          <input
            id="media-input"
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={e => addMediaFiles(Array.from(e.target.files))}
          />
          {mediaFiles.length === 0 ? (
            <div className="text-center text-slate-600">
              <div className="text-2xl">📎</div>
              <div className="text-xs">Click or drag-and-drop photos & videos ({mediaFiles.length}/5)</div>
            </div>
          ) : (
            <div className="flex gap-2.5 flex-wrap" onClick={e => e.stopPropagation()}>
              {mediaFiles.map((mf, i) => (
                <div key={i} className="relative">
                  {mf.type === 'image' && mf.preview ? (
                    <img src={mf.preview} alt="" className="w-20 h-20 object-cover rounded-xl" />
                  ) : (
                    <div className="w-20 h-20 bg-slate-800 rounded-xl flex items-center justify-center text-2xl">🎬</div>
                  )}
                  {mf.progress > 0 && mf.progress < 100 && (
                    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-amber-500 rounded-b-xl" style={{ width: `${mf.progress}%` }} />
                  )}
                  <button type="button" onClick={() => removeMedia(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 border-none text-white cursor-pointer flex items-center justify-center text-[10px] p-0">✕</button>
                </div>
              ))}
              {mediaFiles.length < 5 && (
                <div onClick={() => document.getElementById('media-input')?.click()} className="w-20 h-20 border-2 border-dashed border-slate-700/30 rounded-xl flex items-center justify-center text-2xl cursor-pointer text-amber-500 hover:border-amber-500/30 transition-colors">+</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Anonymous Toggle */}
      <div className="flex items-center justify-between bg-slate-800/40 border border-slate-700/30 rounded-xl px-4 py-3.5">
        <div>
          <div className="font-semibold text-white text-sm">Submit Anonymously</div>
          <div className="text-[11px] text-slate-500">Hides your name from other students</div>
        </div>
        <label className="flex items-center cursor-pointer">
          <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} className="hidden" />
          <span className={`w-11 h-6 rounded-full relative transition-colors ${isAnonymous ? 'bg-amber-500' : 'bg-slate-700'}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${isAnonymous ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
          </span>
        </label>
      </div>
      {isAnonymous && (
        <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/10 rounded-xl px-3.5 py-2.5 -mt-3">
          <span className="text-xs text-amber-400">ℹ️</span>
          <span className="text-xs text-slate-500">
            Your name will be hidden from other students, but the admin will still be able to see it for accountability and follow-up.
          </span>
        </div>
      )}

      {submitError && <div className="text-red-400 text-xs">{submitError}</div>}

      <button type="submit" className={`w-full py-3.5 bg-amber-500 hover:bg-amber-400 text-black border-none rounded-xl font-bold cursor-pointer text-sm transition-colors ${canSubmit ? 'opacity-100' : 'opacity-50 cursor-not-allowed'}`} disabled={!canSubmit}>
        {submitting ? '⏳ Submitting...' : '📤 Submit Complaint'}
      </button>
    </form>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ onSubmit, hasComplaints }) {
  return (
    <div className="text-center py-12 px-6">
      <div className="text-5xl mb-4">📬</div>
      <div className="font-bold text-lg text-white mb-2">
        {hasComplaints ? 'No complaints match your filter' : 'No Complaints Yet'}
      </div>
      <div className="text-slate-500 text-sm mb-6">
        {hasComplaints ? 'Try a different filter.' : 'Have an issue? Lodge a complaint and we\'ll look into it.'}
      </div>
      {!hasComplaints && (
        <button className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black border-none rounded-xl font-bold cursor-pointer text-sm transition-colors" onClick={onSubmit}>+ Lodge First Complaint</button>
      )}
    </div>
  );
}
